package design

import org.jetbrains.compose.web.css.*
import kotlinx.browser.document
import kotlinx.browser.window

/**
 * Accessibility Guidelines for Schneeblume
 *
 * Provides helper functions and utilities for implementing WCAG 2.1 AA
 * compliant accessibility features.
 *
 * Key Features:
 * - Skip links for keyboard navigation
 * - Focus management in modals
 * - Screen reader announcements
 * - ARIA attributes helpers
 * - Keyboard navigation support
 *
 * WCAG 2.1 AA Requirements:
 * - Color contrast: 4.5:1 for normal text, 3:1 for large text
 * - Focus indicators: 3:1 contrast ratio
 * - Touch targets: minimum 44x44px
 * - Keyboard accessibility: all functions operable via keyboard
 */
object AccessibilityGuidelines {
    // ==================== SKIP LINKS ====================

    /**
     * Skip link target IDs
     *
     * Common targets for skip links:
     * - main: Main content area
     * - navigation: Navigation menu
     * - search: Search input
     */
    enum class SkipLinkTarget(val id: String, val label: String) {
        MAIN("main-content", "メインコンテンツへスキップ"),
        NAVIGATION("navigation", "ナビゲーションへスキップ"),
        SEARCH("search", "検索へスキップ")
    }

    /**
     * Get skip link href for a target
     *
     * @param target The skip link target
     * @return The href attribute value
     */
    fun skipLinkHref(target: SkipLinkTarget): String = "#${target.id}"

    /**
     * Ensure target element has tabindex="-1" for skip link to work
     *
     * @param targetId The ID of the target element
     */
    fun markSkipLinkTarget(targetId: String) {
        val element = document.getElementById(targetId)
        element?.setAttribute("tabindex", "-1")
    }

    // ==================== FOCUS MANAGEMENT ====================

    /**
     * Focus trap state
     *
     * Tracks focusable elements for modal focus trapping
     */
    private var focusTrapElements: List<dynamic> = emptyList()
    private var previousFocus: dynamic = null

    /**
     * Get all focusable elements within a container
     *
     * Focusable elements: a, button, input, textarea, select, details, [tabindex]
     *
     * @param containerId The ID of the container element
     * @return List of focusable elements
     */
    fun getFocusableElements(containerId: String): List<dynamic> {
        val container = document.getElementById(containerId) ?: return emptyList()
        val focusableSelectors = listOf(
            "a[href]",
            "button:not([disabled])",
            "textarea:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "details",
            "[tabindex]:not([tabindex=\"-1\"])"
        ).joinToString(", ")

        @Suppress("UNCHECKED_CAST")
        return container.querySelectorAll(focusableSelectors) as List<dynamic>
    }

    /**
     * Trap focus within a container (for modals)
     *
     * Cycles focus within the container when Tab/Shift+Tab is pressed
     *
     * @param containerId The ID of the container
     */
    fun trapFocus(containerId: String) {
        focusTrapElements = getFocusableElements(containerId)
        val firstElement = focusTrapElements.firstOrNull()
        val lastElement = focusTrapElements.lastOrNull()

        if (firstElement != null) {
            firstElement.focus()
        }

        // Listen for Tab and Shift+Tab
        val handler = { event: dynamic ->
            if (event.key == "Tab") {
                if (focusTrapElements.isEmpty()) {
                    // Do nothing, just return
                } else {
                    val first = focusTrapElements.first()
                    val last = focusTrapElements.last()

                    if (event.shiftKey) {
                        // Shift+Tab: wrap from first to last
                        if (document.activeElement == first) {
                            event.preventDefault()
                            last.focus()
                        }
                    } else {
                        // Tab: wrap from last to first
                        if (document.activeElement == last) {
                            event.preventDefault()
                            first.focus()
                        }
                    }
                }
            }
        }

        document.addEventListener("keydown", handler)
    }

    /**
     * Release focus trap
     *
     * Removes event listeners and clears trapped elements
     */
    fun releaseFocus() {
        focusTrapElements = emptyList()
    }

    /**
     * Save current focused element
     *
     * Call before opening a modal to restore focus on close
     */
    fun saveFocus() {
        previousFocus = document.activeElement
    }

    /**
     * Restore previously saved focus
     *
     * Call after closing a modal
     */
    fun restoreFocus() {
        previousFocus?.asDynamic()?.focus?.invoke()
        previousFocus = null
    }

    /**
     * Focus first element in a container
     *
     * @param containerId The ID of the container
     */
    fun focusFirst(containerId: String) {
        val elements = getFocusableElements(containerId)
        elements.firstOrNull()?.focus()
    }

    /**
     * Focus last element in a container
     *
     * @param containerId The ID of the container
     */
    fun focusLast(containerId: String) {
        val elements = getFocusableElements(containerId)
        elements.lastOrNull()?.focus()
    }

    // ==================== SCREEN READER ====================

    /**
     * Live region priorities
     */
    enum class LiveRegion(val ariaLive: String) {
        POLITE("polite"),
        ASSERTIVE("assertive"),
        OFF("off")
    }

    /**
     * Create or get a live region for announcements
     *
     * Live regions allow screen readers to announce dynamic content changes
     *
     * @param regionId The ID for the live region
     * @param priority The announcement priority
     * @return The live region element
     */
    fun getLiveRegion(regionId: String, priority: LiveRegion = LiveRegion.POLITE): dynamic {
        var region = document.getElementById(regionId)
        if (region == null) {
            region = document.createElement("div")
            region.id = regionId
            region.setAttribute("aria-live", priority.ariaLive)
            region.setAttribute("aria-atomic", "true")
            // Set inline styles using attributes
            region.setAttribute("style", "position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;")
            document.body?.appendChild(region)
        }
        return region
    }

    /**
     * Announce a message to screen readers
     *
     * Uses a live region to make the announcement
     *
     * @param message The message to announce
     * @param priority The announcement priority
     * @param regionId The ID of the live region to use
     */
    fun announce(message: String, priority: LiveRegion = LiveRegion.POLITE, regionId: String = "a11y-announcement") {
        val region = getLiveRegion(regionId, priority)
        region.textContent = ""
        // Force reflow to reset the live region
        region.offsetHeight
        region.textContent = message
    }

    /**
     * Announce an error message
     *
     * Uses assertive priority for immediate announcement
     *
     * @param errorMessage The error message to announce
     */
    fun announceError(errorMessage: String) {
        announce("エラー: $errorMessage", LiveRegion.ASSERTIVE, "a11y-error")
    }

    /**
     * Announce a success message
     *
     * @param successMessage The success message to announce
     */
    fun announceSuccess(successMessage: String) {
        announce("成功: $successMessage", LiveRegion.POLITE, "a11y-success")
    }

    // ==================== ARIA ATTRIBUTES ====================

    /**
     * Common ARIA attributes
     */
    object Aria {
        // Landmark roles
        const val BANNER = "banner"
        const val NAVIGATION = "navigation"
        const val MAIN = "main"
        const val COMPLEMENTARY = "complementary"
        const val CONTENTINFO = "contentinfo"
        const val SEARCH = "search"
        const val FORM = "form"
        const val ARTICLE = "article"
        const val REGION = "region"

        // Live region roles
        const val ALERT = "alert"
        const val STATUS = "status"
        const val LOG = "log"
        const val MARQUEE = "marquee"
        const val TIMER = "timer"

        // Widget roles
        const val BUTTON = "button"
        const val LINK = "link"
        const val CHECKBOX = "checkbox"
        const val RADIO = "radio"
        const val COMBOBOX = "combobox"
        const val LISTBOX = "listbox"
        const val MENU = "menu"
        const val MENUITEM = "menuitem"
        const val TAB = "tab"
        const val TABPANEL = "tabpanel"
        const val DIALOG = "dialog"

        // States and properties
        const val EXPANDED = "aria-expanded"
        const val CHECKED = "aria-checked"
        const val SELECTED = "aria-selected"
        const val HIDDEN = "aria-hidden"
        const val DISABLED = "aria-disabled"
        const val REQUIRED = "aria-required"
        const val INVALID = "aria-invalid"
        const val PRESSED = "aria-pressed"
        const val BUSY = "aria-busy"
        const val GRABBED = "aria-grabbed"

        // Relationships
        const val CONTROLS = "aria-controls"
        const val DESCRIBEDBY = "aria-describedby"
        const val LABELLEDBY = "aria-labelledby"
        const val FLOWTO = "aria-flowto"
        const val HASPOPUP = "aria-haspopup"
        const val OWNS = "aria-owns"
    }

    /**
     * Set ARIA attribute on an element
     *
     * @param elementId The ID of the element
     * @param attribute The ARIA attribute name
     * @param value The attribute value
     */
    fun setAria(elementId: String, attribute: String, value: String) {
        val element = document.getElementById(elementId)
        element?.setAttribute(attribute, value)
    }

    /**
     * Set ARIA boolean attribute
     *
     * @param elementId The ID of the element
     * @param attribute The ARIA attribute name
     * @param value The boolean value
     */
    fun setAriaBool(elementId: String, attribute: String, value: Boolean) {
        setAria(elementId, attribute, value.toString())
    }

    /**
     * Remove ARIA attribute
     *
     * @param elementId The ID of the element
     * @param attribute The ARIA attribute name
     */
    fun removeAria(elementId: String, attribute: String) {
        val element = document.getElementById(elementId)
        element?.removeAttribute(attribute)
    }

    // ==================== KEYBOARD NAVIGATION ====================

    /**
     * Keyboard keys
     */
    object Key {
        const val ENTER = "Enter"
        const val SPACE = " "
        const val ESCAPE = "Escape"
        const val TAB = "Tab"
        const val ARROW_UP = "ArrowUp"
        const val ARROW_DOWN = "ArrowDown"
        const val ARROW_LEFT = "ArrowLeft"
        const val ARROW_RIGHT = "ArrowRight"
        const val HOME = "Home"
        const val END = "End"
        const val PAGE_UP = "PageUp"
        const val PAGE_DOWN = "PageDown"
    }

    /**
     * Add keyboard navigation handler
     *
     * @param elementId The ID of the element
     * @param handler The keyboard handler function (returns true if handled)
     */
    fun addKeyboardHandler(elementId: String, handler: (key: String, event: dynamic) -> Boolean) {
        val element = document.getElementById(elementId)
        element?.addEventListener("keydown", { event ->
            val key = event.asDynamic()?.key?.toString() ?: ""
            if (handler(key, event)) {
                event.asDynamic()?.preventDefault()
            }
        })
    }

    /**
     * Add escape key handler
     *
     * Commonly used for closing modals and dropdowns
     *
     * @param elementId The ID of the element
     * @param onEscape The callback function
     */
    fun addEscapeHandler(elementId: String, onEscape: () -> Unit) {
        addKeyboardHandler(elementId) { key, _ ->
            if (key == Key.ESCAPE) {
                onEscape()
                true
            } else {
                false
            }
        }
    }

    /**
     * Add arrow key handler for list navigation
     *
     * @param elementId The ID of the list container
     * @param items List of item IDs in order
     * @param onSelect Callback when an item is selected
     */
    fun addListNavigation(elementId: String, items: List<String>, onSelect: (String) -> Unit) {
        var currentIndex = -1

        addKeyboardHandler(elementId) { key, _ ->
            when (key) {
                Key.ARROW_DOWN -> {
                    currentIndex = if (currentIndex < items.size - 1) currentIndex + 1 else 0
                    document.getElementById(items[currentIndex])?.asDynamic()?.focus?.invoke()
                    true
                }
                Key.ARROW_UP -> {
                    currentIndex = if (currentIndex > 0) currentIndex - 1 else items.size - 1
                    document.getElementById(items[currentIndex])?.asDynamic()?.focus?.invoke()
                    true
                }
                Key.HOME -> {
                    currentIndex = 0
                    document.getElementById(items[currentIndex])?.asDynamic()?.focus?.invoke()
                    true
                }
                Key.END -> {
                    currentIndex = items.size - 1
                    document.getElementById(items[currentIndex])?.asDynamic()?.focus?.invoke()
                    true
                }
                Key.ENTER, Key.SPACE -> {
                    if (currentIndex >= 0) {
                        onSelect(items[currentIndex])
                    }
                    true
                }
                else -> false
            }
        }
    }

    // ==================== REDUCED MOTION ====================

    /**
     * Check if user prefers reduced motion
     *
     * @return true if reduced motion is preferred
     */
    fun prefersReducedMotion(): Boolean {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    }

    /**
     * Listen for reduced motion preference changes
     *
     * @param callback Called when preference changes
     */
    fun onReducedMotionChange(callback: (prefersReduced: Boolean) -> Unit) {
        window.matchMedia("(prefers-reduced-motion: reduce)")
            .addEventListener("change", { event ->
                callback(event.asDynamic()?.matches ?: false)
            })
    }

    // ==================== COLOR CONTRAST ====================

    /**
     * Calculate relative luminance of a color
     *
     * Used for contrast ratio calculations
     *
     * @param hexColor The hex color value (e.g., "#4A90E2")
     * @return The relative luminance (0-1)
     */
    fun calculateLuminance(hexColor: String): Double {
        val hex = hexColor.replace("#", "")
        val r = hex.substring(0, 2).toInt(16) / 255.0
        val g = hex.substring(2, 4).toInt(16) / 255.0
        val b = hex.substring(4, 6).toInt(16) / 255.0

        val channelLuminance = { c: Double ->
            if (c <= 0.03928) c / 12.92
            else js("Math.pow((c + 0.055) / 1.055, 2.4)") as Double
        }

        return 0.2126 * channelLuminance(r) +
               0.7152 * channelLuminance(g) +
               0.0722 * channelLuminance(b)
    }

    /**
     * Calculate contrast ratio between two colors
     *
     * WCAG AA requirements:
     * - Normal text: 4.5:1
     * - Large text (18px+): 3:1
     * - UI components: 3:1
     *
     * @param foreground Hex color for foreground
     * @param background Hex color for background
     * @return The contrast ratio
     */
    fun calculateContrastRatio(foreground: String, background: String): Double {
        val l1 = calculateLuminance(foreground)
        val l2 = calculateLuminance(background)
        val lighter = maxOf(l1, l2)
        val darker = minOf(l1, l2)
        return (lighter + 0.05) / (darker + 0.05)
    }

    /**
     * Check if contrast ratio meets WCAG AA standard
     *
     * @param ratio The contrast ratio
     * @param isLargeText Whether the text is large (18px or 14px bold)
     * @return true if WCAG AA compliant
     */
    fun meetsWCAG_AA(ratio: Double, isLargeText: Boolean = false): Boolean {
        return if (isLargeText) {
            ratio >= 3.0
        } else {
            ratio >= 4.5
        }
    }

    // ==================== LABELS ====================

    /**
     * Ensure input has a visible label or aria-label
     *
     * @param inputId The ID of the input element
     * @param label The label text (if not using <label> element)
     */
    fun ensureLabel(inputId: String, label: String? = null) {
        val input = document.getElementById(inputId) ?: return

        // Check if associated label exists
        val hasLabel = document.querySelector("label[for=\"$inputId\"]") != null
        val hasAriaLabel = input.getAttribute("aria-label") != null
        val hasAriaLabelledBy = input.getAttribute("aria-labelledby") != null

        if (!hasLabel && !hasAriaLabel && !hasAriaLabelledBy && label != null) {
            input.setAttribute("aria-label", label)
        }
    }

    /**
     * Mark field as required for screen readers
     *
     * @param inputId The ID of the input element
     */
    fun markRequired(inputId: String) {
        val input = document.getElementById(inputId)
        input?.setAttribute("aria-required", "true")
    }

    /**
     * Mark field as invalid
     *
     * @param inputId The ID of the input element
     * @param errorMessage The error message (used for aria-errormessage)
     * @param errorId The ID of the error message element
     */
    fun markInvalid(inputId: String, errorMessage: String, errorId: String) {
        val input = document.getElementById(inputId)
        input?.setAttribute("aria-invalid", "true")
        input?.setAttribute("aria-errormessage", errorId)
        announceError(errorMessage)
    }

    /**
     * Clear invalid state
     *
     * @param inputId The ID of the input element
     */
    fun clearInvalid(inputId: String) {
        val input = document.getElementById(inputId)
        input?.removeAttribute("aria-invalid")
        input?.removeAttribute("aria-errormessage")
    }

    // ==================== INITIALIZATION ====================

    /**
     * Initialize accessibility features
     *
     * Call this on app initialization to set up global accessibility
     */
    fun init() {
        // Create live regions
        getLiveRegion("a11y-announcement", LiveRegion.POLITE)
        getLiveRegion("a11y-error", LiveRegion.ASSERTIVE)
        getLiveRegion("a11y-success", LiveRegion.POLITE)

        // Mark skip link targets
        SkipLinkTarget.values().forEach { target ->
            markSkipLinkTarget(target.id)
        }

        js("console.log('Accessibility initialized')")
    }
}
